import { type CSSProperties } from 'react';
import { ButtonBase, IconButton, ListItem, Stack, TextField, Typography } from '@mui/material';
import { getIconComponent } from '../../utils/icons';
import { TestIds } from '../../constants';
import { useInlineRename } from '../../hooks/useInlineRename';
import type { Favorite } from '../../services/favoritesStorage';
import type { SortableItemRenderProps } from './SortableList';

const EditIcon = getIconComponent('Edit');
const CloseIcon = getIconComponent('Close');
const DragHandleIcon = getIconComponent('DragHandle');

interface FavoriteManagerRowProps {
  favorite: Favorite;
  onRename: (pathname: string, label: string) => void;
  onRemove: (pathname: string) => void;
  /** Drag wiring from SortableItem; absent for the static drag overlay preview. */
  setNodeRef?: (node: HTMLElement | null) => void;
  style?: CSSProperties;
  dragHandleAttributes?: SortableItemRenderProps['attributes'];
  dragHandleListeners?: SortableItemRenderProps['listeners'];
}

/**
 * A single favorite presented as a shaded card inside the Favorites Manager modal. This is
 * the one place favorites are managed: a drag handle to reorder, the editable label (click to
 * rename), the destination URL, and a remove button. Rename/remove/reorder edit the modal's
 * local draft; nothing persists until the user saves.
 */
export function FavoriteManagerRow({
  favorite,
  onRename,
  onRemove,
  setNodeRef,
  style,
  dragHandleAttributes,
  dragHandleListeners,
}: FavoriteManagerRowProps) {
  const { isEditing, draft, setDraft, startEditing, commit, handleKeyDown } = useInlineRename({
    initialValue: favorite.label,
    onCommit: (label) => onRename(favorite.pathname, label),
  });

  const category =
    favorite.pathname
      .split('/')
      .filter(Boolean)[0]
      ?.replace(/-/g, ' ')
      .replace(/^\w/, (c) => c.toUpperCase()) ?? '';

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      data-testid={TestIds.FAVORITES_MANAGER_ROW}
      disableGutters
      disablePadding
      sx={{ '&:not(:last-child)': { mb: 1 } }}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap={1.5}
        sx={{ width: '100%', border: 1, borderColor: 'divider', borderRadius: 2, px: 2, py: 1.5 }}
      >
        <IconButton
          size="small"
          aria-label={`Reorder ${favorite.label}`}
          sx={{ cursor: 'grab', color: 'text.secondary', touchAction: 'none', flexShrink: 0 }}
          {...dragHandleAttributes}
          {...dragHandleListeners}
        >
          <DragHandleIcon sx={{ fontSize: 18 }} />
        </IconButton>

        <Stack sx={{ minWidth: 0, flexGrow: 1 }}>
          {isEditing ? (
            <TextField
              autoFocus
              fullWidth
              size="small"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={commit}
              slotProps={{ htmlInput: { 'aria-label': `Rename ${favorite.label}` } }}
            />
          ) : (
            <>
              {category && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {category}
                </Typography>
              )}
              <ButtonBase
                onClick={startEditing}
                title="Click to rename"
                aria-label={`Rename ${favorite.label}`}
                sx={{ justifyContent: 'flex-start', gap: 0.5, maxWidth: '100%', borderRadius: 1 }}
              >
                <Typography variant="body2" fontWeight={600} noWrap>
                  {favorite.label}
                </Typography>
                <EditIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0, ml: 1 }} />
              </ButtonBase>
            </>
          )}
        </Stack>

        <IconButton
          size="small"
          aria-label={`Remove ${favorite.label}`}
          onClick={() => onRemove(favorite.pathname)}
          sx={{ flexShrink: 0 }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Stack>
    </ListItem>
  );
}
